import { useEffect, useId, useRef, useState } from 'react';

import { useQuestionInteractionState } from '@/features/content/hooks/useQuestionInteractionState';
import { MathText } from '@/features/content/MathText';
import type { UserRole } from '@/features/courses/useCourse';

import { AnswerActionButton } from './AnswerActionButton';
import { CheckedAnswerTag } from './CheckedAnswerTag';
import type { NumericalQuestionData } from './questionNormalizer';
import type { NormalizedAnswerInput } from './useQuestionSubmission';
import styles from './NumericalQuestion.module.css';

interface NumericalQuestionProps {
  areSolutionsVisible: boolean;
  hideStats?: boolean;
  /** The answer is kept on the device until the server has it: no "Saved" tag. */
  isAnswerKept?: boolean;
  isSectionClosed: boolean;
  isSectionOpen: boolean;
  onCheck(answer: NormalizedAnswerInput): Promise<void>;
  /** Told whether the value on screen differs from the one stored. */
  onEntryChange?(isUnsaved: boolean): void;
  onSave(answer: NormalizedAnswerInput): Promise<void>;
  pending: boolean;
  question: NumericalQuestionData;
  /** The request the service worker kept and the server has since refused. */
  refusedSubmissionId?: string;
  role: UserRole;
  showActions?: boolean;
}

// The same number as the server keeps it is the same answer. It stores a float32 and
// answers with the shortest form of that, so 9.80 comes back as 9.8 and 0.3333333333
// as 0.33333334, and that is nothing the student changed.
function asComparedNumber(text: string) {
  const trimmed = text.trim();
  const stored = Math.fround(Number(trimmed));

  return trimmed.length > 0 && Number.isFinite(stored) ? String(stored) : trimmed;
}

export function NumericalQuestion({
  areSolutionsVisible,
  hideStats = false,
  isAnswerKept = false,
  isSectionClosed,
  isSectionOpen,
  onCheck,
  onEntryChange,
  onSave,
  pending,
  question,
  refusedSubmissionId,
  role,
  showActions = true,
}: NumericalQuestionProps) {
  const [value, setValue] = useState(question.inputValue);
  const [savedValue, setSavedValue] = useState(question.inputValue);
  const [checkedValue, setCheckedValue] = useState(
    question.attemptsUsed > 0 && question.feedback?.status !== 'saved'
      ? asComparedNumber(question.inputValue)
      : '',
  );
  const inputId = useId();
  const unitsId = useId();
  const verdictId = useId();
  const previousQuestionIdRef = useRef(question.id);

  useEffect(() => {
    const isSameQuestion = previousQuestionIdRef.current === question.id;
    previousQuestionIdRef.current = question.id;

    // What the student has changed and not saved stays while they can still change
    // it. An older answer replayed from the queue put itself back in the box, and a
    // Check then graded that and used an attempt.
    if (!isSameQuestion || !isEntryUnsaved || interaction.isAnswerLocked) {
      setValue(question.inputValue);
    }

    setSavedValue(question.inputValue);
    setCheckedValue(
      question.attemptsUsed > 0 && question.feedback?.status !== 'saved'
        ? asComparedNumber(question.inputValue)
        : '',
    );
  }, [
    question.attemptsUsed,
    question.feedback?.status,
    question.id,
    question.inputValue,
  ]);

  const hasAnswer = value.trim().length > 0;
  const normalizedValue = asComparedNumber(value);
  const normalizedSavedValue = asComparedNumber(savedValue);
  const hasSavedAnswer = hasAnswer && normalizedValue === normalizedSavedValue;
  // A check stores the value it grades, so Check takes the value on screen,
  // saved or not, unless it is the one last checked (issue #92).
  const isAnswerAlreadyChecked = hasAnswer && normalizedValue === checkedValue;
  const isEntryUnsaved = normalizedValue !== normalizedSavedValue;
  const hasSelectionChangedFromSaved =
    hasAnswer && normalizedValue !== normalizedSavedValue;

  useEffect(() => {
    onEntryChange?.(isEntryUnsaved);
  }, [isEntryUnsaved, onEntryChange]);

  const earnedPoints =
    question.feedback?.earnedPoints ?? question.scoreEarned;
  const hasEarnedMaxPoints =
    earnedPoints !== undefined &&
    question.pointsPossible > 0 &&
    earnedPoints >= question.pointsPossible;
  const interaction = useQuestionInteractionState({
    attemptsAllowed: question.maxAttempts,
    attemptsUsed: question.attemptsUsed,
    areSolutionsVisible,
    hasCheckableAnswer: hasAnswer && !isAnswerAlreadyChecked,
    hasEarnedMaxPoints,
    hasCheckedAtLeastOnce:
      question.attemptsUsed > 0 && question.feedback?.status !== 'saved',
    hasSavedAnswer,
    hasSelectionChangedFromSaved,
    hideStatsAndSolutions: hideStats,
    hintSetting: question.hintSetting,
    isPractice: !showActions || !question.isAssigned,
    isSectionClosed,
    isSectionOpen,
    isSingleAttemptMode: question.isSingleAttemptMode,
    role,
  });

  // Kept by the service worker, this answer was recorded as saved. The server has now
  // refused it, which changes nothing stored, so the effect above does not run. It stays
  // on screen, as unsaved, while it can still be changed; a box that cannot shows what
  // is stored.
  useEffect(() => {
    if (!refusedSubmissionId) {
      return;
    }

    if (interaction.isAnswerLocked) {
      setValue(question.inputValue);
    }

    setSavedValue(question.inputValue);
  }, [interaction.isAnswerLocked, question.inputValue, refusedSubmissionId]);

  const answer: NormalizedAnswerInput = {
    currentRandomNumericalAnswer: question.currentRandomNumericalAnswer,
    currentRandomNumericalAnswerExtraExponent:
      question.currentRandomNumericalAnswerExtraExponent,
    kind: 'numerical',
    value,
  };

  async function handleSave() {
    if (!hasAnswer) {
      return;
    }

    // Saving always saves — never grades. Single-attempt questions have no Check
    // button, but their Save must NOT reveal correctness or consume the answer:
    // students revise and re-save until the due date (the backend may still score
    // the save, but that is not surfaced until the section closes).
    //
    // onSave rejects when the answer was not stored (the card has told the
    // student). Then nothing below runs: the entry stays unsaved, and Save
    // stays available. It used to be marked "Saved" whatever happened.
    try {
      await onSave(answer);
    } catch {
      return;
    }

    setSavedValue(value);
  }

  async function handleCheck() {
    if (!hasAnswer) {
      return;
    }

    // As for Save: a check that was not stored leaves no verdict behind.
    try {
      await onCheck(answer);
    } catch {
      return;
    }

    setSavedValue(value);
    setCheckedValue(asComparedNumber(value));
  }

  const showActionRow =
    showActions && (interaction.showSaveButton || interaction.showCheckButton);
  const answerState =
    role === 'student' &&
    question.isAssigned &&
    normalizedValue.length > 0 &&
    normalizedValue === checkedValue &&
    question.feedback?.status !== 'saved'
      ? question.feedback?.status === 'correct'
        ? 'correct'
        : question.feedback?.status === 'partial'
          ? 'partial'
          : question.feedback?.status === 'incorrect'
          ? 'incorrect'
          : undefined
      : role === 'student' &&
          question.isAssigned &&
          hasSavedAnswer &&
          !isAnswerKept &&
          normalizedValue !== checkedValue
        ? 'saved'
        : undefined;

  return (
    <div className={styles.form}>
      <label className={styles.label} htmlFor={inputId}>
        Numerical answer
      </label>
      <div className={styles.inputFrame} data-answer-state={answerState}>
        <div className={styles.inputRow}>
          <input
            aria-describedby={
              [
                question.units ? unitsId : null,
                answerState && answerState !== 'saved' ? verdictId : null,
              ]
                .filter(Boolean)
                .join(' ') || undefined
            }
            className={styles.input}
            disabled={interaction.isAnswerLocked}
            id={inputId}
            onChange={(event) => setValue(event.target.value)}
            type="number"
            value={value}
          />
          {question.units ? (
            <span className={styles.units} id={unitsId}>
              <MathText text={question.units} />
            </span>
          ) : null}
        </div>
        {answerState === 'saved' ? (
          <span className={styles.answerTag}>Saved</span>
        ) : null}
        {answerState && answerState !== 'saved' ? (
          <CheckedAnswerTag
            className={styles.answerTag}
            iconClassName={styles.answerTagIcon}
            id={verdictId}
            verdict={answerState}
          />
        ) : null}
      </div>
      {showActionRow ? (
        <div className={styles.actions}>
          {interaction.showSaveButton ? (
            <AnswerActionButton
              onPress={() => {
                void handleSave();
              }}
              questionId={question.id}
              unavailable={!hasAnswer || interaction.isSaveButtonDisabled || pending}
              variant="secondary"
            >
              Save answer
            </AnswerActionButton>
          ) : null}
          {interaction.showCheckButton ? (
            <AnswerActionButton
              onPress={() => {
                void handleCheck();
              }}
              questionId={question.id}
              unavailable={!hasAnswer || interaction.isCheckButtonDisabled || pending}
              variant="primary"
            >
              Check answer
            </AnswerActionButton>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
